import { useMemo } from 'react';

import { format, getCurrency, type FormatType } from '@/utils/format';
import { formatDate } from '@/utils/months';
import { usePrefs } from '@/store/prefs';
import { useUiStore } from '@/store/app';

/**
 * Binds loot-core's `format(amount, type)` to the budget's currency pref and
 * the privacy ("hide amounts") toggle.
 */
export function useFormat() {
  const currencyCode = usePrefs(state => state.budget.currency);
  const hideAmounts = useUiStore(state => state.hideAmounts);

  return useMemo(
    () =>
      (amount: number | null | undefined, type: FormatType = 'financial') =>
        format(amount, type, getCurrency(currencyCode), hideAmounts),
    [currencyCode, hideAmounts],
  );
}

/** Date rendering bound to the `longDateFormat` pref, e.g. `MM/dd/yyyy`. */
export function useDateFormat() {
  const pattern = usePrefs(state => state.budget.longDateFormat);
  return useMemo(
    () =>
      (date: string | Date | null | undefined) =>
        formatDate(date, pattern ?? 'yyyy-MM-dd'),
    [pattern],
  );
}
